/* ============================================================================
   Bro Glo — Accessory PDP JavaScript
   ============================================================================
   Handles: tier selection, gallery, ATC with Functions Creator tagging,
   pricing engine, shipping bar, inline cross-sell checkboxes, urgency counter,
   ticker cycling, accordion, sticky ATC.
   No shade selector (single-variant products).
   Zero dependencies · ADA compliant · Fast.
   ============================================================================ */

(function () {
  'use strict';

  const BrogloPdpAccessory = {

    /* ── State ── */
    state: {},

    /* ── Init ── */
    init(sectionId) {
      const data = window.brogloPdpAccessoryData?.[sectionId];
      if (!data) return;

      this.state[sectionId] = {
        data,
        currentVariantId: data.currentVariantId,
        currentTier: data.defaultTier || 2,
        currentSlide: 0
      };

      this.initGallery(sectionId);
      this.selectTier(sectionId, this.state[sectionId].currentTier);
      this.initStickyAtc(sectionId);
      this.initTickerCycle(sectionId);
    },

    /* ======================== TIER SELECTION ======================== */
    selectTier(sectionId, tier) {
      const state = this.state[sectionId];
      if (!state) return;

      state.currentTier = tier;

      // Update card active states + ARIA
      const container = document.getElementById(`brogloPdpTiers-${sectionId}`);
      if (container) {
        container.querySelectorAll('.broglo-tier__card').forEach(card => {
          const cardTier = parseInt(card.dataset.tier);
          const isActive = cardTier === tier;
          card.classList.toggle('broglo-tier__card--active', isActive);
          card.setAttribute('aria-checked', isActive ? 'true' : 'false');
        });
      }

      this.updatePricing(sectionId);

      // Screen reader announcement
      const tierData = state.data.tiers[tier];
      this.announce(sectionId, `${tierData.label} selected. ${tierData.qty} items, ${tierData.discount}% off.`);
    },

    /* ======================== CROSS-SELL TOGGLE ======================== */
    toggleXsell(sectionId, card) {
      const isChecked = card.classList.contains('broglo-acc-xsell__card--checked');
      card.classList.toggle('broglo-acc-xsell__card--checked', !isChecked);
      card.setAttribute('aria-checked', (!isChecked).toString());

      this.updatePricing(sectionId);

      const name = card.querySelector('.broglo-acc-xsell__name')?.textContent || 'item';
      this.announce(sectionId, `${name} ${!isChecked ? 'added to' : 'removed from'} your order.`);
    },

    /* ======================== CROSS-SELL TOTAL ======================== */
    getXsellTotal(sectionId) {
      const section = document.getElementById(`BrogloPdp-${sectionId}`);
      if (!section) return 0;

      let total = 0;
      section.querySelectorAll('.broglo-acc-xsell__card--checked').forEach(card => {
        total += parseInt(card.dataset.price) || 0;
      });
      return total;
    },

    /* ======================== PRICING ENGINE ======================== */
    updatePricing(sectionId) {
      const state = this.state[sectionId];
      if (!state) return;

      const price = state.data.variantPrice;
      const tier = state.currentTier;
      const tierData = state.data.tiers[tier];
      if (!tierData) return;

      const totalFull = price * tierData.qty;
      const discountMult = 1 - (tierData.discount / 100);
      const totalDiscounted = Math.round(totalFull * discountMult);
      const savings = totalFull - totalDiscounted;

      // Update all tier card prices
      this.updateTierCardPrices(sectionId, price);

      // Main price display
      const priceEl = document.getElementById(`brogloPdpPrice-${sectionId}`);
      if (priceEl) priceEl.textContent = this.formatMoney(totalDiscounted);

      // Compare price
      const compareEl = document.getElementById(`brogloPdpCompare-${sectionId}`);
      if (compareEl) {
        if (tier > 1 && savings > 0) {
          compareEl.textContent = this.formatMoney(totalFull);
          compareEl.classList.add('visible');
        } else {
          compareEl.classList.remove('visible');
        }
      }

      // ATC button price
      const atcBtn = document.getElementById(`brogloPdpAtcBtn-${sectionId}`);
      const atcPrice = document.getElementById(`brogloPdpAtcPrice-${sectionId}`);
      if (atcBtn && atcPrice) {
        const xsellTotal = this.getXsellTotal(sectionId);
        const atcTotal = totalDiscounted + xsellTotal;

        if (tier > 1 && savings > 0) {
          atcPrice.innerHTML = ` — <span class="broglo-pdp__atc-strike">${this.formatMoney(totalFull + xsellTotal)}</span> <span class="broglo-pdp__atc-final">${this.formatMoney(atcTotal)}</span>`;
        } else if (xsellTotal > 0) {
          atcPrice.innerHTML = ` — ${this.formatMoney(atcTotal)}`;
        } else {
          atcPrice.innerHTML = ` — ${this.formatMoney(totalDiscounted)}`;
        }
      }

      // Shipping bar (include cross-sell total)
      const xsellTotal = this.getXsellTotal(sectionId);
      this.updateShippingBar(sectionId, totalDiscounted + xsellTotal);

      // Sticky bar
      const stickyPrice = document.getElementById(`brogloPdpStickyPrice-${sectionId}`);
      if (stickyPrice) stickyPrice.textContent = this.formatMoney(totalDiscounted + xsellTotal);
    },

    updateTierCardPrices(sectionId, price) {
      const state = this.state[sectionId];
      const tiers = state.data.tiers;

      // Tier 1
      const t1Price = document.getElementById(`brogloPdpTier1Price-${sectionId}`);
      if (t1Price) t1Price.textContent = this.formatMoney(price * tiers[1].qty);

      // Tier 2
      const t2Full = price * tiers[2].qty;
      const t2Disc = Math.round(t2Full * (1 - tiers[2].discount / 100));
      const t2Each = Math.round(t2Disc / tiers[2].qty);

      const t2Compare = document.getElementById(`brogloPdpTier2Compare-${sectionId}`);
      if (t2Compare) t2Compare.textContent = this.formatMoney(t2Full);
      const t2Price = document.getElementById(`brogloPdpTier2Price-${sectionId}`);
      if (t2Price) t2Price.textContent = this.formatMoney(t2Disc);
      const t2Ea = document.getElementById(`brogloPdpTier2Each-${sectionId}`);
      if (t2Ea) t2Ea.textContent = this.formatMoney(t2Each) + ' each';

      // Tier 3
      const t3Full = price * tiers[3].qty;
      const t3Disc = Math.round(t3Full * (1 - tiers[3].discount / 100));
      const t3Each = Math.round(t3Disc / tiers[3].qty);

      const t3Compare = document.getElementById(`brogloPdpTier3Compare-${sectionId}`);
      if (t3Compare) t3Compare.textContent = this.formatMoney(t3Full);
      const t3Price = document.getElementById(`brogloPdpTier3Price-${sectionId}`);
      if (t3Price) t3Price.textContent = this.formatMoney(t3Disc);
      const t3Ea = document.getElementById(`brogloPdpTier3Each-${sectionId}`);
      if (t3Ea) t3Ea.textContent = this.formatMoney(t3Each) + ' each';
    },

    /* ======================== SHIPPING BAR ======================== */
    updateShippingBar(sectionId, total) {
      const state = this.state[sectionId];
      if (!state) return;

      const threshold = state.data.freeShipThreshold;
      const fill = document.getElementById(`brogloPdpShipFill-${sectionId}`);
      const text = document.getElementById(`brogloPdpShipText-${sectionId}`);
      if (!fill || !text) return;

      const pct = Math.min(100, Math.round((total / threshold) * 100));
      fill.style.width = pct + '%';

      if (total >= threshold) {
        text.innerHTML = '🎉 <strong style="color:#2D9B5E">You qualify for free shipping!</strong>';
      } else {
        const remaining = threshold - total;
        text.innerHTML = `Add <strong>${this.formatMoney(remaining)}</strong> more for free shipping`;
      }
    },

    /* ======================== ADD TO CART ======================== */
    async addToCart(sectionId) {
      const state = this.state[sectionId];
      if (!state) return;

      const atcBtn = document.getElementById(`brogloPdpAtcBtn-${sectionId}`);

      // Guard against double-click while request is in flight
      if (state._adding) return;
      state._adding = true;

      if (atcBtn) {
        atcBtn.setAttribute('aria-disabled', 'true');
        atcBtn.setAttribute('aria-busy', 'true');
      }

      const tier = state.currentTier;
      const tierData = state.data.tiers[tier];
      const prop = state.data.discountProperty;
      const tieredMode = state.data.tieredMode;
      const hasDiscount = tieredMode && tierData.discount > 0;
      const items = [];

      // Only tag with discount property when tiered pricing is ON and discount > 0
      const buildProps = () => {
        if (!prop || !hasDiscount) return undefined;
        return { ['_' + prop]: 'true' };
      };

      // Main product × tier quantity
      items.push({
        id: state.currentVariantId,
        quantity: tierData.qty,
        properties: buildProps()
      });

      // Cross-sell products (checked ones)
      const section = document.getElementById(`BrogloPdp-${sectionId}`);
      if (section) {
        section.querySelectorAll('.broglo-acc-xsell__card--checked').forEach(card => {
          const variantId = parseInt(card.dataset.variantId);
          if (variantId) {
            items.push({
              id: variantId,
              quantity: 1
            });
          }
        });
      }

      // Clean undefined properties
      items.forEach(item => { if (!item.properties) delete item.properties; });

      try {
        console.log('[Bro Glo Accessory ATC] Tier:', tier, 'TieredMode:', tieredMode, 'Items:', JSON.stringify(items));

        // Only clear previous tier items if tiered pricing is active
        if (prop && hasDiscount) {
          try {
            const cartResp = await fetch(
              (window.Shopify?.routes?.root || '/') + 'cart.js',
              { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
            );
            const cart = await cartResp.json();
            const tagKey = '_' + prop;
            const taggedLines = cart.items.filter(ci => ci.properties && ci.properties[tagKey]);
            if (taggedLines.length > 0) {
              const updates = {};
              taggedLines.forEach(line => { updates[line.key] = 0; });
              await fetch(
                (window.Shopify?.routes?.root || '/') + 'cart/update.js',
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                  body: JSON.stringify({ updates })
                }
              );
            }
          } catch (clearErr) {
            console.warn('[Bro Glo Accessory] Could not clear previous tier items:', clearErr);
          }
        }

        const response = await fetch(
          (window.Shopify?.routes?.root || '/') + 'cart/add.js',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items })
          }
        );

        const result = await response.json();
        console.log('[Bro Glo Accessory ATC] Response:', response.status, result);

        if (!response.ok || (result.status && result.status >= 400)) {
          console.error('ATC error:', result.description || result.message);
          this.showAtcFeedback(sectionId, 'error');
          return;
        }

        this.showAtcFeedback(sectionId, 'success');

        // Only set cart attributes when tiered pricing is active with a discount
        if (prop && hasDiscount) {
          try {
            await fetch(
              (window.Shopify?.routes?.root || '/') + 'cart/update.js',
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({
                  attributes: {
                    '_broglo_acc_tier': tier.toString(),
                    '_broglo_acc_tier_discount': tierData.discount.toString()
                  }
                })
              }
            );
          } catch (attrErr) {
            console.warn('[Bro Glo Accessory] Could not set cart attributes:', attrErr);
          }
        }

        // Open cart drawer immediately (no artificial delay)
        this.openCartDrawer();

      } catch (err) {
        console.error('ATC fetch error:', err);
        this.showAtcFeedback(sectionId, 'error');
      } finally {
        state._adding = false;
        if (atcBtn) {
          atcBtn.removeAttribute('aria-disabled');
          atcBtn.removeAttribute('aria-busy');
        }
      }
    },

    showAtcFeedback(sectionId, type) {
      const atcBtn = document.getElementById(`brogloPdpAtcBtn-${sectionId}`);
      if (!atcBtn) return;

      const originalHtml = atcBtn.innerHTML;

      if (type === 'success') {
        atcBtn.innerHTML = '✓ Added!';
        atcBtn.style.background = '#2D9B5E';
        this.announce(sectionId, 'Items added to cart successfully.');
      } else {
        atcBtn.innerHTML = 'Error — Try Again';
        atcBtn.style.background = '#e11d48';
        this.announce(sectionId, 'Error adding items to cart. Please try again.');
      }

      setTimeout(() => {
        atcBtn.innerHTML = originalHtml;
        atcBtn.style.background = '';
      }, 600);
    },

    openCartDrawer() {
      if (typeof BWCart !== 'undefined' && BWCart.refresh) {
        BWCart.refresh(true);
      } else if (typeof BWCart !== 'undefined' && BWCart.open) {
        BWCart.open();
      } else if (typeof CartDrawer !== 'undefined' && CartDrawer.open) {
        CartDrawer.open();
      } else {
        window.location.href = '/cart';
      }
    },

    /* ======================== TICKER CYCLING ======================== */
    initTickerCycle(sectionId) {
      const el = document.getElementById(`brogloPdpTicker-${sectionId}`);
      let countEl = document.getElementById(`brogloPdpTickerCount-${sectionId}`);
      if (!el || !countEl) return;

      const data = window.brogloPdpAccessoryData?.[sectionId];
      const cfg = data?.tickerCount || {};
      const baseCount = cfg.base || 312;
      const weeklyCap = cfg.cap || 900;
      const minInterval = (cfg.minInterval || 20) * 1000;
      const maxInterval = (cfg.maxInterval || 45) * 1000;
      const minInc = cfg.minInc || 1;
      const maxInc = cfg.maxInc || 3;

      // Weekly reset — get Monday of current week
      const now = new Date();
      const day = now.getDay();
      const monday = new Date(now);
      monday.setDate(now.getDate() - ((day + 6) % 7));
      const weekKey = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;

      const storageKey = `broglo_ticker_acc_${sectionId}`;
      let count;

      try {
        const saved = JSON.parse(localStorage.getItem(storageKey));
        if (saved && saved.week === weekKey && typeof saved.count === 'number' && saved.count >= baseCount) {
          const elapsed = Math.floor((Date.now() - (saved.ts || Date.now())) / 30000);
          const catchUp = Math.min(elapsed, 15);
          count = Math.min(saved.count + catchUp, weeklyCap);
        } else {
          count = baseCount + Math.floor(Math.random() * 8);
        }
      } catch (e) {
        count = baseCount + Math.floor(Math.random() * 8);
      }

      countEl.textContent = count;

      const save = () => {
        try {
          localStorage.setItem(storageKey, JSON.stringify({ count, ts: Date.now(), week: weekKey }));
        } catch (e) { }
      };
      save();

      const scheduleNext = () => {
        const delay = minInterval + Math.floor(Math.random() * (maxInterval - minInterval));
        setTimeout(() => {
          if (count >= weeklyCap) return;
          count += minInc + Math.floor(Math.random() * (maxInc - minInc + 1));
          count = Math.min(count, weeklyCap);
          countEl.textContent = count;
          save();
          scheduleNext();
        }, delay);
      };
      scheduleNext();

      // Ticker message rotation (if multiple messages configured)
      const msgs = data?.tickerMessages?.filter(m => m && m.trim()) || [];
      if (msgs.length > 1) {
        let i = 0;
        el.style.transition = 'opacity 0.3s';
        setInterval(() => {
          i = (i + 1) % msgs.length;
          el.style.opacity = 0;
          setTimeout(() => {
            el.innerHTML = msgs[i].replace('{count}', `<span id="brogloPdpTickerCount-${sectionId}">${count}</span>`);
            countEl = document.getElementById(`brogloPdpTickerCount-${sectionId}`);
            el.style.opacity = 1;
          }, 300);
        }, 4000);
      }
    },

    /* ======================== GALLERY ======================== */
    initGallery(sectionId) {
      const slidesEl = document.getElementById(`brogloPdpSlides-${sectionId}`);
      if (!slidesEl) return;

      if (window.innerWidth < 768) {
        slidesEl.addEventListener('scroll', () => {
          const slides = slidesEl.querySelectorAll('.broglo-pdp__slide');
          const scrollLeft = slidesEl.scrollLeft;
          let closest = 0;
          let minDist = Infinity;
          slides.forEach((slide, i) => {
            if (slide.classList.contains('variant-hidden')) return;
            const dist = Math.abs(slide.offsetLeft - scrollLeft - slidesEl.offsetWidth * 0.06);
            if (dist < minDist) { minDist = dist; closest = i; }
          });
          this.setSlide(sectionId, closest, false);
        }, { passive: true });
      }
    },

    setSlide(sectionId, index, scroll) {
      const state = this.state[sectionId];
      if (!state) return;
      state.currentSlide = index;

      // Dots
      const dotsContainer = document.getElementById(`brogloPdpDots-${sectionId}`);
      if (dotsContainer) {
        dotsContainer.querySelectorAll('.broglo-pdp__dot').forEach(dot => {
          dot.classList.toggle('active', parseInt(dot.dataset.slide) === index);
        });
      }

      // Thumbs — horizontal-only scroll (no vertical page jump)
      const thumbsContainer = document.getElementById(`brogloPdpThumbs-${sectionId}`);
      if (thumbsContainer) {
        thumbsContainer.querySelectorAll('.broglo-pdp__thumb').forEach(thumb => {
          const isActive = parseInt(thumb.dataset.slide) === index;
          thumb.classList.toggle('active', isActive);
          if (isActive && !thumb.classList.contains('variant-hidden')) {
            const thumbLeft = thumb.offsetLeft - thumbsContainer.offsetLeft;
            const thumbCenter = thumbLeft - (thumbsContainer.offsetWidth / 2) + (thumb.offsetWidth / 2);
            thumbsContainer.scrollTo({ left: thumbCenter, behavior: 'smooth' });
          }
        });
      }

      // Desktop: transform
      if (scroll !== false && window.innerWidth >= 768) {
        const slidesEl = document.getElementById(`brogloPdpSlides-${sectionId}`);
        if (slidesEl) {
          const allSlides = Array.from(slidesEl.querySelectorAll('.broglo-pdp__slide'));
          let visibleOffset = 0;
          for (let i = 0; i < allSlides.length; i++) {
            if (i === index) break;
            if (!allSlides[i].classList.contains('variant-hidden')) visibleOffset++;
          }
          slidesEl.style.transform = `translateX(-${visibleOffset * 100}%)`;
        }
      }
    },

    goToSlide(sectionId, index) {
      this.setSlide(sectionId, index, true);

      // Mobile: horizontal-only scroll
      if (window.innerWidth < 768) {
        const slidesEl = document.getElementById(`brogloPdpSlides-${sectionId}`);
        const slide = slidesEl?.querySelectorAll('.broglo-pdp__slide')[index];
        if (slide && slidesEl) {
          const slideLeft = slide.offsetLeft - slidesEl.offsetLeft;
          slidesEl.scrollTo({ left: slideLeft, behavior: 'smooth' });
        }
      }
    },

    /* ======================== THUMBNAIL SCROLLING ======================== */
    scrollThumbs(sectionId, direction) {
      const thumbsEl = document.getElementById(`brogloPdpThumbs-${sectionId}`);
      if (!thumbsEl) return;
      const scrollAmount = 164 * direction;
      thumbsEl.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setTimeout(() => this.updateThumbArrows(sectionId), 350);
    },

    updateThumbArrows(sectionId) {
      const thumbsEl = document.getElementById(`brogloPdpThumbs-${sectionId}`);
      const prevBtn = document.getElementById(`brogloPdpThumbPrev-${sectionId}`);
      const nextBtn = document.getElementById(`brogloPdpThumbNext-${sectionId}`);
      if (!thumbsEl) return;

      const maxScroll = thumbsEl.scrollWidth - thumbsEl.clientWidth;
      if (prevBtn) prevBtn.disabled = thumbsEl.scrollLeft <= 2;
      if (nextBtn) nextBtn.disabled = thumbsEl.scrollLeft >= maxScroll - 2;
    },

    /* ======================== ACCORDION ======================== */
    toggleAccordion(trigger) {
      const content = trigger.nextElementSibling;
      const isOpen = trigger.classList.contains('open');

      trigger.classList.toggle('open');
      content.classList.toggle('open');
      trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');

      if (!isOpen) {
        content.style.maxHeight = content.scrollHeight + 'px';
      } else {
        content.style.maxHeight = '0';
      }
    },

    /* ======================== STICKY ATC ======================== */
    initStickyAtc(sectionId) {
      const sticky = document.getElementById(`brogloPdpStickyAtc-${sectionId}`);
      const atcBtn = document.getElementById(`brogloPdpAtcBtn-${sectionId}`);
      if (!sticky || !atcBtn) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            sticky.classList.remove('visible');
          } else {
            const rect = entry.boundingClientRect;
            if (rect.top < 0) {
              sticky.classList.add('visible');
            } else {
              sticky.classList.remove('visible');
            }
          }
        });
      }, { threshold: 0 });

      observer.observe(atcBtn);
    },

    /* ======================== HELPERS ======================== */
    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    },

    /* ======================== ACCESSIBILITY ======================== */
    announce(sectionId, message) {
      const el = document.getElementById(`brogloPdpSrAnnounce-${sectionId}`);
      if (!el) return;
      el.textContent = '';
      requestAnimationFrame(() => { el.textContent = message; });
    }
  };

  /* ── Expose globally ── */
  window.BrogloPdpAccessory = BrogloPdpAccessory;

  /* ── Auto-init on DOM ready ── */
  function autoInit() {
    const sections = document.querySelectorAll('[data-pdp-type="accessory"]');
    sections.forEach(section => {
      const sectionId = section.dataset.sectionId;
      if (sectionId && window.brogloPdpAccessoryData?.[sectionId]) {
        BrogloPdpAccessory.init(sectionId);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }

  /* ── Click delegation for dots/thumbs ── */
  document.addEventListener('click', e => {
    const dot = e.target.closest('.broglo-pdp__dot');
    if (dot) {
      const sectionId = dot.closest('[data-pdp-type="accessory"]')?.dataset.sectionId;
      if (sectionId) BrogloPdpAccessory.goToSlide(sectionId, parseInt(dot.dataset.slide));
    }

    const thumb = e.target.closest('.broglo-pdp__thumb');
    if (thumb) {
      const sectionId = thumb.closest('[data-pdp-type="accessory"]')?.dataset.sectionId;
      if (sectionId) BrogloPdpAccessory.goToSlide(sectionId, parseInt(thumb.dataset.slide));
    }
  });
})();
